// SVG port of GaugeChartView.chart: observed stage (solid), forecast (dashed,
// bridged to the last observation), flood-category rules with labels, a "now"
// rule, axes, a legend, and a crosshair tooltip for pointer and keyboard.
import { useMemo } from 'react';
import type { CSSProperties, JSX, PointerEvent } from 'react';
import { formatDateTime, formatNumber } from '../../lib/format';
import { closeArea, extent, formatAxisValue, linearScale, monotonePath, nearestIndex, niceTicks, paddedDomain, timeTicks } from './chartMath';
import type { LinearScale } from './chartMath';
import { DataTable, MARGIN, Tooltip, XAxis, YGrid, pointerX, useActiveIndex, useContainerWidth } from './chartParts';
import type { TooltipRow } from './chartParts';
import { describeStageChart, forecastLine, forecastSeries, observedSeries } from './model';
import type { StageReading, Threshold } from './model';

const HEIGHT = 280;
const LABEL_HEIGHT = 11;
const SERIES_COLOR = { observed: 'var(--chart-observed)', forecast: 'var(--chart-forecast)' } as const;
const SERIES_LABEL = { observed: 'Observed', forecast: 'Forecast' } as const;

interface PlacedThreshold {
  threshold: Threshold;
  ruleY: number;
  textY: number;
}

/** Labels sit above their rule, right-aligned (Swift `.top, .trailing`); one that would overlap the label below it drops under its rule. */
function placeThresholdLabels(thresholds: Threshold[], y: LinearScale, top: number): PlacedThreshold[] {
  const sorted = [...thresholds].sort((a, b) => a.value - b.value);
  const out: PlacedThreshold[] = [];
  let previousTextY = Infinity;
  for (const threshold of sorted) {
    const ruleY = y(threshold.value);
    let textY = ruleY - 4;
    if (textY + LABEL_HEIGHT > previousTextY || textY - LABEL_HEIGHT < top - 14) textY = ruleY + LABEL_HEIGHT;
    out.push({ threshold, ruleY, textY });
    previousTextY = textY;
  }
  return out;
}

export function StageChart({
  visible,
  thresholds,
  unit,
  now,
  emptyMessage,
}: {
  /** Readings already limited to the selected time range, sorted by time. */
  visible: StageReading[];
  thresholds: Threshold[];
  unit: string;
  now: Date;
  emptyMessage: string;
}): JSX.Element {
  const { ref, width } = useContainerWidth();
  const { index, setActive, onKeyDown } = useActiveIndex(visible.length, Math.max(0, visible.findLastIndex((r) => r.series === 'observed')));

  const model = useMemo(() => {
    const observed = observedSeries(visible);
    const forecast = forecastLine(visible);
    const forecastCount = forecastSeries(visible).length;
    const times = visible.map((r) => r.time.getTime());
    const nowMs = now.getTime();
    let [tMin, tMax] = extent([...times, nowMs]) ?? [nowMs, nowMs];
    if (tMin === tMax) {
      tMin -= 3_600_000;
      tMax += 3_600_000;
    }
    const [yLo, yHi] = paddedDomain(...(extent([...visible.map((r) => r.stage), ...thresholds.map((t) => t.value)]) ?? [0, 1]), 0.08);
    let yTicks = niceTicks(yLo, yHi, 5);
    if (yTicks.length < 2) yTicks = [yLo, yHi];
    const plotRight = width - MARGIN.right;
    const plotBottom = HEIGHT - MARGIN.bottom;
    const x = linearScale(tMin, tMax, MARGIN.left, plotRight);
    const y = linearScale(yTicks[0], yTicks[yTicks.length - 1], plotBottom, MARGIN.top);
    const plotWidth = plotRight - MARGIN.left;
    const xt = timeTicks(tMin, tMax, Math.max(2, Math.min(6, Math.floor(plotWidth / 80))));
    const toPoint = (r: StageReading) => ({ x: x(r.time.getTime()), y: y(r.stage) });
    const observedPoints = observed.map(toPoint);
    const observedPath = monotonePath(observedPoints);
    return {
      observed,
      forecastCount,
      times,
      x,
      y,
      yTicks,
      xTicks: xt.ticks,
      stepMs: xt.stepMs,
      plotRight,
      plotBottom,
      observedPath,
      observedArea: closeArea(observedPath, observedPoints, plotBottom),
      forecastPath: forecast.length > 1 ? monotonePath(forecast.map(toPoint)) : '',
      labels: placeThresholdLabels(thresholds, y, MARGIN.top),
      nowX: x(nowMs),
      description: describeStageChart(visible, thresholds, unit),
    };
  }, [visible, thresholds, unit, now, width]);

  if (visible.length === 0) {
    return (
      <div className="gd-chart-empty" role="status">
        {emptyMessage}
      </div>
    );
  }

  const { x, y, times, plotRight, plotBottom } = model;
  const activeTime = index != null ? times[index] : null;
  const activeRows: StageReading[] = activeTime != null ? visible.filter((r) => r.time.getTime() === activeTime) : [];
  const activeX = activeTime != null ? x(activeTime) : 0;

  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    setActive(nearestIndex(times, x.invert(pointerX(e, width))));
  };

  const tooltipRows: TooltipRow[] = activeRows.map((r) => ({
    value: `${formatNumber(r.stage, 2)} ${unit}`,
    label: SERIES_LABEL[r.series],
    color: SERIES_COLOR[r.series],
    dashed: r.series === 'forecast',
  }));

  return (
    <div className="gd-chart" ref={ref}>
      <svg
        width={width}
        height={HEIGHT}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        role="img"
        aria-label={model.description}
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setActive(null)}
        onKeyDown={onKeyDown}
        onBlur={() => setActive(null)}
      >
        <YGrid ticks={model.yTicks} y={y} x0={MARGIN.left} x1={plotRight} format={(v) => `${formatAxisValue(v)} ${unit}`} />
        <XAxis ticks={model.xTicks} stepMs={model.stepMs} x={x} x0={MARGIN.left} x1={plotRight} yTop={MARGIN.top} yBottom={plotBottom} height={HEIGHT} />

        {/* Flood-category reference lines, labelled so identity never rests on the hue alone. */}
        {model.labels.map(({ threshold, ruleY, textY }) => (
          <g key={threshold.key}>
            <line className={`gd-threshold gd-threshold-${threshold.key}`} x1={MARGIN.left} x2={plotRight} y1={ruleY} y2={ruleY} />
            <text className="gd-threshold-label" x={plotRight - 2} y={textY} textAnchor="end">
              {threshold.name} {threshold.value.toFixed(1)}
            </text>
          </g>
        ))}

        {/* Observed → forecast boundary. */}
        <line className="gd-now" x1={model.nowX} x2={model.nowX} y1={MARGIN.top} y2={plotBottom} />

        {model.observedArea ? <path className="gd-area gd-area-observed" d={model.observedArea} /> : null}
        {model.observedPath ? <path className="gd-line gd-line-observed" d={model.observedPath} /> : null}
        {model.forecastPath ? <path className="gd-line gd-line-forecast" d={model.forecastPath} /> : null}

        {activeTime != null ? (
          <g>
            <line className="gd-crosshair" x1={activeX} x2={activeX} y1={MARGIN.top} y2={plotBottom} />
            {activeRows.map((r) => (
              <circle key={r.series} className="gd-active-dot" cx={activeX} cy={y(r.stage)} r={4} style={{ fill: SERIES_COLOR[r.series] }} />
            ))}
          </g>
        ) : null}
      </svg>

      {activeTime != null && tooltipRows.length ? <Tooltip x={activeX} width={width} time={new Date(activeTime)} rows={tooltipRows} /> : null}

      <ul className="gd-legend">
        <li>
          <span className="gd-key" style={{ '--c': SERIES_COLOR.observed } as CSSProperties} aria-hidden="true" />
          Observed
        </li>
        {model.forecastCount > 0 ? (
          <li>
            <span className="gd-key is-dashed" style={{ '--c': SERIES_COLOR.forecast } as CSSProperties} aria-hidden="true" />
            Forecast
          </li>
        ) : null}
      </ul>

      <DataTable
        summary={`Data table (${visible.length} readings)`}
        columns={[{ label: 'Time' }, { label: 'Series' }, { label: `Stage (${unit})`, numeric: true }]}
        rows={() =>
          visible.map((r) => (
            <tr key={`${r.series}-${r.time.getTime()}`}>
              <td>{formatDateTime(r.time)}</td>
              <td>{SERIES_LABEL[r.series]}</td>
              <td className="gd-num">{formatNumber(r.stage, 2)}</td>
            </tr>
          ))
        }
      />
    </div>
  );
}
